import { Alert } from 'react-native';
import * as Notifications from 'expo-notifications';

import type { SummaryVariant, HomeSummary } from '@/types/home-summary';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import { FamilyContextCollector } from '@/lib/family/FamilyContextCollector';
import { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import { LocalCalendarMemberMappingStore } from '@/lib/local/LocalCalendarMemberMappingStore';
import { ScopedAsyncStorage, deviceIdentifier } from '@/lib/storage';

/**
 * DeveloperUtilities
 *
 * Provides utility methods for development and testing.
 * Singleton class for triggering manual AI summaries and notifications.
 *
 * @example
 * const devUtils = DeveloperUtilities.getInstance();
 * const variant = await devUtils.showVariantPicker();
 * if (variant) {
 *   await devUtils.generateSummaryAndNotify(variant, token, timezone);
 * }
 */
export class DeveloperUtilities {
  private static instance: DeveloperUtilities | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): DeveloperUtilities {
    if (!DeveloperUtilities.instance) {
      DeveloperUtilities.instance = new DeveloperUtilities();
    }
    return DeveloperUtilities.instance;
  }

  /**
   * Shows an alert to pick a summary variant
   * @returns Promise resolving to selected variant or null if cancelled
   */
  public showVariantPicker(): Promise<SummaryVariant | null> {
    return new Promise((resolve) => {
      Alert.alert(
        'Developer: Generate Summary',
        'Choose which summary variant to generate',
        [
          {
            text: 'Cancel',
            style: 'cancel',
            onPress: () => resolve(null),
          },
          {
            text: 'Morning Brief',
            onPress: () => resolve('morning-brief'),
          },
          {
            text: 'Evening Recap',
            onPress: () => resolve('evening-recap'),
          },
        ],
        { cancelable: true, onDismiss: () => resolve(null) },
      );
    });
  }

  /**
   * Generates an AI summary and sends a test notification
   * @param variant - The summary variant to generate
   * @param token - Authentication token
   * @param timezone - User's timezone (defaults to system timezone)
   * @returns Promise resolving to the generated summary or null on error
   */
  public async generateSummaryAndNotify(
    variant: SummaryVariant,
  ): Promise<HomeSummary | null> {
    try {
      // Request notification permissions if needed
      const permissionStatus = await Notifications.getPermissionsAsync();
      if (!permissionStatus.granted) {
        const { status } = await Notifications.requestPermissionsAsync();
        if (status !== 'granted') {
          console.warn('[DeveloperUtilities] Notification permission denied');
        }
      }

      // Generate summary via API
      const summary = await this.generateSummary(variant);

      if (!summary) {
        throw new Error('Failed to generate summary');
      }

      // Send test notification
      await this.sendTestNotification(variant, summary.headline);

      return summary;
    } catch (error) {
      console.error('[DeveloperUtilities] generateSummaryAndNotify failed:', error);
      throw error;
    }
  }

  /**
   * Calls the API to generate a summary
   * @param variant - The summary variant
   * @param token - Authentication token
   * @returns Promise resolving to generated summary or null
   */
  private async generateSummary(
    variant: SummaryVariant,
  ): Promise<HomeSummary | null> {
    try {
      // Collect family context for family-aware summaries
      let familyPayload: Record<string, unknown> = {};
      try {
        const deviceId = await deviceIdentifier.getDeviceId();
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        const userId = await AsyncStorage.getItem('fimfam-ai:summary:user-id');
        if (userId && deviceId) {
          const storage = new ScopedAsyncStorage(userId, deviceId);
          const familyContext = await FamilyContextCollector.collect(
            new LocalFamilyMembersStore(storage),
            new LocalCalendarMemberMappingStore(storage),
          );
          if (familyContext) {
            familyPayload = {
              familyMembers: familyContext.familyMembers,
              calendarMemberMappings: familyContext.calendarMemberMappings,
              meFamilyMemberId: familyContext.meFamilyMemberId,
            };
          }
        }
      } catch (err) {
        console.warn('[DeveloperUtilities] Failed to collect family context:', err);
      }

      const response = await authenticatedFetch.fetch('/api/home-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant,
          force: true,
          ...familyPayload,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
      }

      const data = (await response.json()) as { summary: HomeSummary | null };
      return data.summary;
    } catch (error) {
      console.error('[DeveloperUtilities] generateSummary failed:', error);
      throw error;
    }
  }

  /**
   * Sends a test notification
   * @param variant - The summary variant
   * @param headline - The summary headline to include in notification
   */
  public async sendTestNotification(
    variant: SummaryVariant,
    headline?: string,
  ): Promise<void> {
    try {
      const title = variant === 'morning-brief'
        ? 'Morning brief ready'
        : 'Evening recap ready';

      const body = headline
        ? headline
        : variant === 'morning-brief'
          ? "Open FimFam to review today's plan."
          : "See what was finished and what's coming tomorrow.";

      await Notifications.scheduleNotificationAsync({
        content: {
          title,
          body,
          sound: 'default',
          data: { variant, isDeveloperTest: true },
        },
        trigger: null, // Send immediately
      });

      console.log('[DeveloperUtilities] Test notification sent:', { variant, title });
    } catch (error) {
      console.error('[DeveloperUtilities] sendTestNotification failed:', error);
      throw error;
    }
  }

  /**
   * Shows a success alert
   * @param variant - The generated variant
   * @param headline - The summary headline
   */
  public showSuccessAlert(variant: SummaryVariant, headline: string): void {
    const variantLabel = variant === 'morning-brief' ? 'Morning Brief' : 'Evening Recap';
    Alert.alert(
      'Summary Generated!',
      `${variantLabel} generated successfully.\n\n"${headline}"\n\nCheck your notifications!`,
      [{ text: 'OK' }],
    );
  }

  /**
   * Shows an error alert
   * @param error - The error that occurred
   */
  public showErrorAlert(error: Error): void {
    Alert.alert(
      'Generation Failed',
      `Failed to generate summary:\n\n${error.message}`,
      [{ text: 'OK' }],
    );
  }
}
